import { AddonsTab } from './tabs/AddonsTab.js';
import { ConnecteursTab } from './tabs/ConnecteursTab.js';
import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { Link, Navigate, useNavigate, useParams } from 'react-router';
import { Tabs } from '@artosera/ui';
import { obtenirGalerie } from '../lib/api.js';
import { AuditTable } from '../components/AuditTable.js';
import { JobsTable } from '../components/JobsTable.js';
import { ApparenceTab } from './tabs/ApparenceTab.js';
import { DomainesTab } from './tabs/DomainesTab.js';
import { IdentiteTab } from './tabs/IdentiteTab.js';
import { MembresTab } from './tabs/MembresTab.js';
import { ModulesTab } from './tabs/ModulesTab.js';
import { RepriseTab } from './tabs/RepriseTab.js';
import { ResiliationTab } from './tabs/ResiliationTab.js';
import { SauvegardesTab } from './tabs/SauvegardesTab.js';
import { SupportTab } from './tabs/SupportTab.js';
import { BadgeTest, InterrupteurEspaceTest } from './EspaceDeTest.js';
import { BandeauMiseEnService } from './Ouvertures/BandeauMiseEnService.js';
import pageStyles from '../styles/page.module.css';
import styles from './GalleryDetail.module.css';

const ONGLETS = [
  'identite',
  'modules',
  'addons',
  'theme',
  'connecteurs',
  'domaines',
  'membres',
  'sauvegardes',
  'reprise',
  'travaux',
  'journal',
  'support',
  'resiliation',
] as const;

type Onglet = (typeof ONGLETS)[number];

function estOnglet(valeur: string | undefined): valeur is Onglet {
  return (ONGLETS as readonly string[]).includes(valeur ?? '');
}

export function GalleryDetail(): JSX.Element {
  const { t } = useTranslation('console');
  const navigate = useNavigate();
  const params = useParams<{ id: string; onglet?: string }>();
  const idGalerie = params.id ?? '';

  const requete = useQuery({
    queryKey: ['console', 'galerie', idGalerie],
    queryFn: () => obtenirGalerie(idGalerie),
    enabled: idGalerie !== '',
  });

  if (!estOnglet(params.onglet)) {
    return <Navigate to={`/galeries/${idGalerie}/identite`} replace />;
  }

  if (requete.isLoading) {
    return <p className={pageStyles.videEtat}>{t('commun.chargement')}</p>;
  }

  if (requete.isError || !requete.data) {
    return (
      <section>
        <p className={pageStyles.videEtat}>{t('fiche.introuvable')}</p>
        <Link to="/galeries" className="lien-cible">
          {t('fiche.retour')}
        </Link>
      </section>
    );
  }

  const galerie = requete.data;

  return (
    <section>
      <p>
        <Link to="/galeries" className="lien-cible">
          {t('fiche.retour')}
        </Link>
      </p>
      <BandeauMiseEnService idGalerie={idGalerie} />
      <div className={pageStyles.entete}>
        <div>
          <h1>
            {galerie.nom}
            {galerie.estTest ? (
              <>
                {' '}
                <BadgeTest />
              </>
            ) : null}
          </h1>
          <p>
            {galerie.slug} ·{' '}
            {t('fiche.creeLe', { date: new Date(galerie.creeLe).toLocaleDateString('fr-FR') })}
          </p>
        </div>
        <InterrupteurEspaceTest idGalerie={idGalerie} estTest={galerie.estTest} />
      </div>

      <div className={styles.onglets}>
        <Tabs
          value={params.onglet}
          onValueChange={(valeur) => navigate(`/galeries/${idGalerie}/${valeur}`)}
          tabs={[
            {
              value: 'identite',
              label: t('fiche.onglets.identite'),
              content: <IdentiteTab idGalerie={idGalerie} galerie={galerie} />,
            },
            {
              value: 'modules',
              label: t('fiche.onglets.modules'),
              content: <ModulesTab idGalerie={idGalerie} galerie={galerie} />,
            },
            {
              value: 'addons',
              label: t('addons.titre'),
              content: <AddonsTab idGalerie={idGalerie} galerie={galerie} />,
            },
            {
              value: 'theme',
              label: t('fiche.onglets.theme'),
              content: <ApparenceTab idGalerie={idGalerie} galerie={galerie} />,
            },
            {
              value: 'connecteurs',
              label: t('fiche.onglets.connecteurs'),
              content: <ConnecteursTab idGalerie={idGalerie} />,
            },
            {
              value: 'domaines',
              label: t('fiche.onglets.domaines'),
              content: <DomainesTab idGalerie={idGalerie} galerie={galerie} />,
            },
            {
              value: 'membres',
              label: t('fiche.onglets.membres'),
              content: <MembresTab galerie={galerie} />,
            },
            {
              value: 'sauvegardes',
              label: t('fiche.onglets.sauvegardes'),
              content: <SauvegardesTab idGalerie={idGalerie} galerie={galerie} />,
            },
            {
              value: 'reprise',
              label: t('fiche.onglets.reprise'),
              content: <RepriseTab idGalerie={idGalerie} />,
            },
            {
              value: 'travaux',
              label: t('fiche.onglets.travaux'),
              content: <JobsTable galerie={galerie.slug} />,
            },
            {
              value: 'journal',
              label: t('fiche.onglets.journal'),
              content: <AuditTable galerie={galerie.slug} />,
            },
            {
              value: 'support',
              label: t('fiche.onglets.support'),
              content: <SupportTab idGalerie={idGalerie} galerie={galerie} />,
            },
            {
              value: 'resiliation',
              label: t('fiche.onglets.resiliation'),
              content: <ResiliationTab idGalerie={idGalerie} galerie={galerie} />,
            },
          ]}
        />
      </div>
    </section>
  );
}
